import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { CURRENT_WORK } from "../data";

const EASE = [0.76, 0, 0.24, 1];

const ProjectFeature = ({ project, index }) => {
  const flip = index % 2 === 1;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center">
      {/* Poster */}
      <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2 lg:col-start-7" : ""}`}>
        <div className={`grid ${project.secondaryPoster ? "grid-cols-2 gap-3 lg:gap-4" : "grid-cols-1"}`}>
          <div className="group relative overflow-hidden border border-foreground/10 aspect-[3/4]">
            <img
              src={project.poster}
              alt={project.title}
              className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-[1.03]"
            />
            <span
              data-testid={`current-status-${project.id}`}
              className="absolute top-4 left-4 bg-primary text-primary-foreground text-[10px] sm:text-xs uppercase tracking-[0.2em] font-bold px-4 py-2"
            >
              {project.status}
            </span>
          </div>
          {project.secondaryPoster && (
            <div className="group relative overflow-hidden border border-foreground/10 aspect-[3/4]">
              <img
                src={project.secondaryPoster}
                alt={`${project.title} — on Bullet`}
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-[1.03]"
              />
            </div>
          )}
        </div>
      </Reveal>

      {/* Info */}
      <div className={`lg:col-span-5 ${flip ? "lg:order-1 lg:col-start-1" : "lg:col-start-8"}`}>
        <Reveal>
          <span className="inline-block bg-primary text-primary-foreground px-4 py-2 text-xs sm:text-sm uppercase tracking-[0.25em] font-bold">
            {project.format}
          </span>
          <h3 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.9]">
            {project.title}
          </h3>
          <p className="mt-6 font-body text-base lg:text-lg text-foreground/60 leading-relaxed max-w-md">
            {project.blurb}
          </p>
          <p
            data-testid={`current-highlight-${project.id}`}
            className="mt-5 font-display font-extrabold text-2xl lg:text-3xl text-primary leading-snug max-w-md"
          >
            Crossed over 15 million views on the platform.
          </p>

          <div className="mt-8 space-y-4 max-w-md">
            {[
              ["Client", project.client],
              ["Format", project.format],
              ["Language", project.language],
            ].map(([k, v]) => (
              <div key={k} className="flex items-start justify-between gap-6 border-t border-foreground/10 pt-4">
                <span className="text-xs uppercase tracking-[0.2em] text-foreground/40">{k}</span>
                <span className="font-body text-sm text-foreground/80 text-right">{v}</span>
              </div>
            ))}
          </div>

          <a
            href="#contact"
            data-testid={`current-cta-${project.id}`}
            className="group mt-10 inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-bold text-foreground hover:text-primary transition-colors"
          >
            Enquire about this project
            <span className="grid place-items-center h-11 w-11 border border-foreground/20 rounded-full group-hover:bg-primary group-hover:border-primary group-hover:text-primary-foreground transition-colors duration-300">
              <ArrowUpRight size={18} />
            </span>
          </a>
        </Reveal>
      </div>
    </div>
  );
};

export const CurrentWork = () => {
  return (
    <section id="current" data-testid="current-section" className="relative pt-6 lg:pt-10 pb-16 lg:pb-24 px-6 lg:px-12">
      <Reveal className="mb-10 lg:mb-16 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <p className="inline-block bg-primary text-primary-foreground px-4 py-2 text-xs sm:text-sm uppercase tracking-[0.25em] font-bold">Now Playing</p>
          <h2 className="mt-6 font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9]">
            Latest
            <br />
            work.
          </h2>
        </div>
        <p className="font-body text-base text-foreground/60 max-w-sm lg:text-right">
          Fresh off the floor — the projects our studio is shipping right now.
        </p>
      </Reveal>

      <div className="flex flex-col gap-16 lg:gap-24">
        {CURRENT_WORK.map((project, i) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <ProjectFeature project={project} index={i} />
          </motion.div>
        ))}
      </div>
    </section>
  );
};
